/**
 * MerchantWithdrawalRequestDetailPage — iter-203 (full-page detail).
 *
 * Route: /dashboard/finance/cloudgm/merchants/withdrawal-requests/:id
 *
 * Renders the complete withdrawal-request detail as a dedicated page
 * (NOT a side drawer per user request). All approve / reject / AI OCR
 * logic lives here.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  CheckCircle, XCircle, Loader2, User, Mail, Phone, Calendar, Hash,
  Building2, CreditCard, UploadCloud, FileText, Eye, Download, X, Receipt,
  ShieldCheck, Shield, BadgeCheck, Sparkles, AlertTriangle, ArrowLeft,
  Trash2, Clock, Pencil,
} from 'lucide-react';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { floorAED, fmtAED } from '../utils/money';
import { DirhamPrice } from '../components/DirhamPrice';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  pending:  { ar: 'قيد المراجعة', en: 'Pending',  cls: 'bg-amber-500/20 text-amber-300 border-amber-500/40' },
  approved: { ar: 'معتمد',        en: 'Approved', cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' },
  rejected: { ar: 'مرفوض',        en: 'Rejected', cls: 'bg-red-500/20 text-red-300 border-red-500/40' },
};

const ALLOWED_MIMES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'application/pdf'];

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result || '';
      const idx = String(result).indexOf(',');
      resolve(idx >= 0 ? String(result).slice(idx + 1) : String(result));
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const normalizeAccountDetails = (ad = {}) => ({
  bankName:       ad.bank_name        || ad.bankName       || ad.bank        || '',
  branch:         ad.branch           || ad.branch_name    || ad.bankBranch  || '',
  iban:           ad.iban             || ad.IBAN           || '',
  accountNumber:  ad.account_number   || ad.accountNumber  || ad.account_no  || '',
  beneficiary:    ad.beneficiary_name || ad.beneficiary    || ad.name        || '',
  swift:          ad.swift_code       || ad.swift          || ad.bic         || '',
});

export default function MerchantWithdrawalRequestDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { token } = useAuth();
  const isAr = language === 'ar';
  const headers = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Approve state
  const [approveFile, setApproveFile] = useState(null);
  const [approveRef, setApproveRef] = useState('');
  const [approveNote, setApproveNote] = useState('');
  const [overrideNote, setOverrideNote] = useState('');
  const [aiData, setAiData] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Reject state (iter-206 — dropdown + notes)
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReasonId, setRejectReasonId] = useState('');
  const [rejectNotes, setRejectNotes] = useState('');
  const [rejectReasons, setRejectReasons] = useState([]);   // dropdown options
  const [rejectReasonsLoading, setRejectReasonsLoading] = useState(false);

  // Receipt viewer
  const [viewingReceipt, setViewingReceipt] = useState(false);

  // iter-264 — Edit-date modal
  const [dateEditOpen, setDateEditOpen] = useState(false);
  const [dateEditVal, setDateEditVal] = useState('');
  const [dateEditReason, setDateEditReason] = useState('');
  const [dateEditSaving, setDateEditSaving] = useState(false);

  const fetchItem = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/withdrawal-requests/${id}`, { headers });
      setItem(res.data?.data || null);
      setNotFound(false);
    } catch (err) {
      console.error('Failed to load withdrawal request:', err);
      if (err.response?.status === 404) {
        setNotFound(true);
      } else {
        toast.error(err.response?.data?.detail || (isAr ? 'فشل تحميل البيانات' : 'Failed to load'));
      }
    } finally {
      setLoading(false);
    }
  }, [id, headers, isAr]);

  useEffect(() => { fetchItem(); }, [fetchItem]);

  const backToList = () => navigate('/dashboard/finance/cloudgm/merchants/withdrawal-requests');

  const handleFilePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error(isAr ? 'حجم الملف يجب ألا يتجاوز 8 ميجابايت' : 'File must be under 8MB');
      e.target.value = '';
      return;
    }
    if (!ALLOWED_MIMES.includes(file.type.toLowerCase())) {
      toast.error(isAr ? 'يُقبل فقط: PNG, JPEG, WEBP, PDF' : 'Only PNG, JPEG, WEBP, PDF allowed');
      e.target.value = '';
      return;
    }
    try {
      const base64 = await readFileAsBase64(file);
      setApproveFile({ base64, mime: file.type, name: file.name, size: file.size });
      setAiData(null);
      setOverrideNote('');
      setAiLoading(true);
      try {
        const res = await axios.post(
          `${BACKEND_URL}/api/admin/withdrawal-requests/extract-receipt`,
          {
            file_base64: base64,
            mime: file.type,
            expected_amount: floorAED(item?.amount),
            expected_currency: item?.currency || 'AED',
          },
          { headers },
        );
        setAiData(res.data?.data || null);
      } catch (err) {
        toast.error(err.response?.data?.detail || (isAr ? 'فشل استخراج بيانات الإيصال' : 'AI extraction failed'));
      } finally {
        setAiLoading(false);
      }
    } catch (err) {
      console.error(err);
      toast.error(isAr ? 'تعذّرت قراءة الملف' : 'Failed to read file');
    }
  };

  const amountMatches = !!aiData?.amount_matches;
  const canApprove = !!approveFile && !processing && (amountMatches || overrideNote.trim().length > 0);

  const handleApprove = async () => {
    if (!approveFile) {
      toast.error(isAr ? 'ارفع إيصال التحويل أولاً' : 'Upload transfer receipt first');
      return;
    }
    if (!amountMatches && !overrideNote.trim()) {
      toast.error(isAr
        ? 'مبلغ الإيصال لا يطابق مبلغ السحب — أضف ملاحظة تجاوز صريحة'
        : 'Receipt amount does not match — add an override note');
      return;
    }
    setProcessing(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/withdrawal-requests/${item.id}/approve`,
        {
          receipt_file: approveFile.base64,
          receipt_mime: approveFile.mime,
          receipt_name: approveFile.name,
          transfer_reference: approveRef.trim(),
          note: approveNote.trim(),
          ai_extracted: aiData || null,
          ai_amount_match: amountMatches,
          override_note: amountMatches ? '' : overrideNote.trim(),
        },
        { headers },
      );
      toast.success(isAr ? 'تم اعتماد الطلب ورفع الإيصال' : 'Approved & receipt uploaded');
      // Reset approve pane and refresh
      setApproveFile(null);
      setApproveRef('');
      setApproveNote('');
      setOverrideNote('');
      setAiData(null);
      fetchItem();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل الاعتماد' : 'Approval failed'));
    } finally {
      setProcessing(false);
    }
  };

  // iter-206 — load dropdown reject reasons when reject mode opens
  const openReject = async () => {
    setRejectMode(true);
    if (rejectReasons.length > 0) return;
    setRejectReasonsLoading(true);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/dropdown-lists`,
        { headers, params: { list_type: 'withdrawal_rejection' } },
      );
      setRejectReasons(res.data?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل تحميل الأسباب' : 'Failed to load reasons'));
    } finally {
      setRejectReasonsLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReasonId) {
      toast.error(isAr ? 'اختر سبب الرفض من القائمة' : 'Select a rejection reason from the list');
      return;
    }
    const chosen = rejectReasons.find((r) => r.id === rejectReasonId);
    if (!chosen) {
      toast.error(isAr ? 'السبب المختار غير صالح' : 'Selected reason is invalid');
      return;
    }
    setProcessing(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/withdrawal-requests/${item.id}/reject`,
        {
          // legacy `reason` field kept for backward-compat: send AR by default
          reason: chosen.label_ar,
          reason_id: chosen.id,
          reason_ar: chosen.label_ar,
          reason_en: chosen.label_en,
          notes: rejectNotes.trim(),
        },
        { headers },
      );
      toast.success(isAr ? 'تم رفض الطلب' : 'Request rejected');
      setRejectMode(false);
      setRejectReasonId('');
      setRejectNotes('');
      fetchItem();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل الرفض' : 'Rejection failed'));
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(isAr ? 'تأكيد حذف هذا الطلب نهائياً؟' : 'Permanently delete this request?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/withdrawal-requests/${item.id}`, { headers });
      toast.success(isAr ? 'تم الحذف' : 'Deleted');
      backToList();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل الحذف' : 'Delete failed'));
    }
  };

  // iter-264 — Open the edit-date modal pre-filled with the current value
  // (converted to a `datetime-local`-compatible string, no seconds, no TZ).
  const openDateEdit = () => {
    const raw = item?.created_at || '';
    let val = '';
    if (raw) {
      const d = new Date(raw);
      if (!Number.isNaN(d.getTime())) {
        const pad = (n) => String(n).padStart(2, '0');
        // datetime-local expects LOCAL time in `YYYY-MM-DDTHH:mm`
        val = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      }
    }
    setDateEditVal(val);
    setDateEditReason('');
    setDateEditOpen(true);
  };

  const handleSaveDate = async () => {
    const reason = dateEditReason.trim();
    if (!reason) {
      toast.error(isAr ? 'أدخل سبب التعديل' : 'Enter a reason for the edit');
      return;
    }
    if (!dateEditVal) {
      toast.error(isAr ? 'اختر التاريخ الجديد' : 'Pick the new date');
      return;
    }
    // Convert the local `datetime-local` value back to ISO with the browser's
    // timezone — new Date() handles that for us.
    const iso = new Date(dateEditVal).toISOString();
    setDateEditSaving(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/withdrawal-requests/${item.id}/date`,
        { new_created_at: iso, reason },
        { headers },
      );
      toast.success(isAr ? 'تم تحديث التاريخ وإشعار التاجر' : 'Date updated & merchant notified');
      setDateEditOpen(false);
      fetchItem();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل التحديث' : 'Update failed'));
    } finally {
      setDateEditSaving(false);
    }
  };

  return (
    <DashboardLayout
      title="تفاصيل طلب السحب"
      titleEn="Withdrawal Request Details"
      subtitle="عرض تفصيلي مع مطابقة إيصال التحويل بالذكاء الاصطناعي"
      subtitleEn="Full detail view with AI-matched transfer receipt"
      screenCode="FIN-CGM-MERCH-WD-DET"
      backPath="/dashboard/finance/cloudgm/merchants/withdrawal-requests"
      breadcrumb={[
        { labelAr: 'المالية',                labelEn: 'Finance',                       path: '/dashboard/finance' },
        { labelAr: 'CLOUDGM FZ LLC',         labelEn: 'CLOUDGM FZ LLC',                path: '/dashboard/finance/cloudgm' },
        { labelAr: 'منصة التجار (المالية)',  labelEn: 'Merchant Platform (Finance)',   path: '/dashboard/finance/cloudgm/merchants' },
        { labelAr: 'طلبات السحب',            labelEn: 'Withdrawal Requests',           path: '/dashboard/finance/cloudgm/merchants/withdrawal-requests' },
        { labelAr: 'تفاصيل الطلب',           labelEn: 'Request Details',               path: null },
      ]}
    >
      <section
        className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
        data-testid="withdrawal-detail-page"
      >
        {/* Header: back + status */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <Button
            onClick={backToList}
            variant="outline"
            className="border-white/10 text-white hover:bg-white/5"
            data-testid="wd-detail-back"
          >
            <ArrowLeft className="h-4 w-4 me-2" />
            {isAr ? 'العودة إلى القائمة' : 'Back to list'}
          </Button>
          {item && (
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded-full text-xs border ${(STATUS_META[item.status] || STATUS_META.pending).cls}`}>
                {isAr ? (STATUS_META[item.status] || STATUS_META.pending).ar : (STATUS_META[item.status] || STATUS_META.pending).en}
              </span>
              <span className="font-mono text-[10px] text-[#71717A]">
                #{String(item.id).slice(0, 8)}
              </span>
              <Button
                onClick={handleDelete}
                variant="outline"
                size="sm"
                className="border-red-500/30 text-red-300 hover:bg-red-500/10"
                data-testid="wd-detail-delete"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
        </div>

        {/* Body */}
        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="wd-detail-loading">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
          </div>
        ) : notFound || !item ? (
          <div className="text-center py-20 bg-white/[0.02] border border-white/10 rounded-2xl">
            <XCircle className="h-14 w-14 mx-auto mb-4 text-red-400/40" />
            <h3 className="text-white font-medium mb-1">
              {isAr ? 'لم يتم العثور على الطلب' : 'Request not found'}
            </h3>
            <p className="text-xs text-[#71717A]">
              {isAr ? 'ربما تم حذفه أو أن الرابط غير صحيح' : 'It may have been deleted or the URL is wrong'}
            </p>
          </div>
        ) : (
          <DetailBody
            item={item}
            isAr={isAr}
            // approve pane
            approveFile={approveFile}
            approveRef={approveRef} setApproveRef={setApproveRef}
            approveNote={approveNote} setApproveNote={setApproveNote}
            overrideNote={overrideNote} setOverrideNote={setOverrideNote}
            aiData={aiData}
            aiLoading={aiLoading}
            amountMatches={amountMatches}
            canApprove={canApprove}
            processing={processing}
            onFilePick={handleFilePick}
            onApprove={handleApprove}
            // reject (iter-206 dropdown-based)
            rejectMode={rejectMode}
            onOpenReject={openReject}
            onCancelReject={() => {
              setRejectMode(false);
              setRejectReasonId('');
              setRejectNotes('');
            }}
            rejectReasons={rejectReasons}
            rejectReasonsLoading={rejectReasonsLoading}
            rejectReasonId={rejectReasonId}
            setRejectReasonId={setRejectReasonId}
            rejectNotes={rejectNotes}
            setRejectNotes={setRejectNotes}
            onReject={handleReject}
            // receipt viewer
            viewingReceipt={viewingReceipt}
            setViewingReceipt={setViewingReceipt}
            // iter-264 — date edit
            onEditDate={openDateEdit}
          />
        )}
      </section>

      {viewingReceipt && item && (
        <ReceiptViewerModal
          item={item}
          isAr={isAr}
          onClose={() => setViewingReceipt(false)}
        />
      )}

      {dateEditOpen && item && (
        <EditDateModal
          isAr={isAr}
          dateVal={dateEditVal}
          setDateVal={setDateEditVal}
          reason={dateEditReason}
          setReason={setDateEditReason}
          saving={dateEditSaving}
          onCancel={() => setDateEditOpen(false)}
          onSave={handleSaveDate}
          originalIso={item.original_created_at}
          currentIso={item.created_at}
          historyCount={(item.date_edit_history || []).length}
        />
      )}
    </DashboardLayout>
  );
}

// ─── body ──────────────────────────────────────────────────────────
function DetailBody({
  item, isAr,
  approveFile, approveRef, setApproveRef, approveNote, setApproveNote,
  overrideNote, setOverrideNote, aiData, aiLoading, amountMatches,
  canApprove, processing, onFilePick, onApprove,
  rejectMode, onOpenReject, onCancelReject, rejectReasons, rejectReasonsLoading,
  rejectReasonId, setRejectReasonId, rejectNotes, setRejectNotes, onReject,
  viewingReceipt: _viewingReceipt, setViewingReceipt,
  onEditDate,
}) {
  const u = item.user || {};
  const ad = normalizeAccountDetails(item.account_details);
  const amount = floorAED(item.amount);
  const isPending = item.status === 'pending';
  const isApproved = item.status === 'approved';
  const isRejected = item.status === 'rejected';
  const hasReceipt = !!(item.receipt_file && item.receipt_mime);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Amount hero */}
      <div className="rounded-xl bg-gradient-to-br from-emerald-500/10 to-emerald-500/0 border border-emerald-500/20 p-5 md:col-span-2">
        <div className="text-[10px] uppercase tracking-wide text-emerald-200/70 mb-1">
          {isAr ? 'المبلغ المطلوب للسحب' : 'Requested withdrawal amount'}
        </div>
        <div className="flex items-baseline gap-2">
          <div className="text-4xl font-bold text-white tabular-nums" data-testid="wd-detail-amount">
            {fmtAED(amount)}
          </div>
          <div className="text-sm text-emerald-200/70">{item.currency || 'AED'}</div>
        </div>
        <div className="mt-2 flex items-center gap-1.5 text-[11px] text-[#A1A1AA] flex-wrap">
          <CreditCard className="h-3 w-3" />
          {isAr ? 'الطريقة:' : 'Method:'} <span className="text-white">{item.method || '—'}</span>
          <span className="mx-1">·</span>
          <Calendar className="h-3 w-3" />
          <span className="text-white" data-testid="wd-detail-created-at">
            {isAr ? 'تاريخ الطلب: ' : 'Requested: '}
            {item.created_at
              ? new Date(item.created_at).toLocaleString(isAr ? 'ar-AE' : 'en-GB', {
                  year: 'numeric', month: 'short', day: '2-digit',
                  hour: '2-digit', minute: '2-digit',
                })
              : '—'}
          </span>
          {onEditDate && (
            <button
              type="button"
              onClick={onEditDate}
              title={isAr ? 'تعديل تاريخ الطلب' : 'Edit request date'}
              className="ms-1 inline-flex items-center gap-1 text-[10px] text-emerald-300 hover:text-emerald-200 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 rounded-full px-2 py-0.5 transition"
              data-testid="wd-detail-edit-date-btn"
            >
              <Pencil className="h-3 w-3" />
              {isAr ? 'تعديل' : 'Edit'}
            </button>
          )}
          {item.original_created_at && item.original_created_at !== item.created_at && (
            <span
              className="text-[10px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-full px-2 py-0.5"
              title={
                (isAr ? 'التاريخ الأصلي: ' : 'Original: ') +
                new Date(item.original_created_at).toLocaleString(isAr ? 'ar-AE' : 'en-GB')
              }
              data-testid="wd-detail-date-edited-badge"
            >
              {isAr ? '✎ عُدِّل بواسطة الإدارة' : '✎ Edited by admin'}
            </span>
          )}
        </div>
      </div>

      {/* iter-207 — SLA countdown banner */}
      <SlaBanner item={item} isAr={isAr} />

      {/* Merchant profile */}
      <SectionCard title={isAr ? 'بيانات التاجر' : 'Merchant profile'} icon={User}>
        <div className="space-y-2">
          <div className="text-white font-medium text-sm" data-testid="wd-detail-merchant-name">
            {u.name || (isAr ? '— بلا اسم —' : '— No name —')}
          </div>
          <div className="flex flex-wrap gap-1.5">
            <AgentBadges user={u} isAr={isAr} />
          </div>
          <div className="space-y-1 text-[11px] text-[#A1A1AA] pt-1">
            {u.email && <ProfileRow icon={Mail} value={u.email} testid="wd-detail-email" />}
            {u.phone && <ProfileRow icon={Phone} value={u.phone} testid="wd-detail-phone" />}
            {u.bgm_number && <ProfileRow icon={Hash} value={u.bgm_number} label={isAr ? 'BGM#' : 'BGM#'} mono />}
            {u.agent_id && <ProfileRow icon={Sparkles} value={u.agent_id} label={isAr ? 'رقم الوكيل' : 'Agent #'} mono />}
          </div>
        </div>
      </SectionCard>

      {/* Bank details */}
      <SectionCard title={isAr ? 'تفاصيل الحساب البنكي' : 'Bank account details'} icon={Building2}>
        {(ad.bankName || ad.branch || ad.iban || ad.accountNumber || ad.beneficiary || ad.swift) ? (
          <div className="space-y-1.5" data-testid="wd-detail-bank">
            <BankRow label={isAr ? 'اسم البنك'   : 'Bank name'}     value={ad.bankName}     testid="wd-bank-name" />
            <BankRow label={isAr ? 'الفرع'       : 'Branch'}        value={ad.branch}       testid="wd-bank-branch" />
            <BankRow label={isAr ? 'المستفيد'    : 'Beneficiary'}   value={ad.beneficiary}  testid="wd-bank-beneficiary" />
            <BankRow label={isAr ? 'رقم الحساب'  : 'Account #'}     value={ad.accountNumber} mono testid="wd-bank-account" />
            <BankRow label={isAr ? 'IBAN'        : 'IBAN'}          value={ad.iban}         mono testid="wd-bank-iban" />
            <BankRow label={isAr ? 'SWIFT / BIC' : 'SWIFT / BIC'}   value={ad.swift}        mono testid="wd-bank-swift" />
          </div>
        ) : (
          <div className="text-[11px] text-[#71717A]">
            {isAr ? 'لا توجد تفاصيل بنكية' : 'No bank details'}
          </div>
        )}
      </SectionCard>

      {/* Rejection */}
      {isRejected && (item.reason || item.reason_ar || item.rejection_notes) && (
        <div className="md:col-span-2 rounded-xl bg-red-500/10 border border-red-500/30 p-4 space-y-2">
          <div className="flex items-center gap-1.5 text-red-200 text-[12px] font-semibold mb-1">
            <XCircle className="h-4 w-4" />
            {isAr ? 'سبب الرفض' : 'Rejection reason'}
          </div>
          <p className="text-[13px] text-red-100 font-medium" data-testid="wd-rejected-reason">
            {isAr
              ? (item.reason_ar || item.reason || item.reason_en)
              : (item.reason_en || item.reason || item.reason_ar)}
          </p>
          {item.rejection_notes && (
            <div className="rounded bg-red-500/5 border border-red-500/20 p-2 mt-1">
              <div className="text-[10px] uppercase tracking-wide text-red-200/70 mb-0.5">
                {isAr ? 'ملاحظات' : 'Notes'}
              </div>
              <p className="text-[11px] text-red-100/90" data-testid="wd-rejected-notes">
                {item.rejection_notes}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Approved banner */}
      {isApproved && hasReceipt && (
        <div className="md:col-span-2 rounded-xl bg-emerald-500/10 border border-emerald-500/30 p-4 space-y-1.5" data-testid="wd-approved-banner">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 text-emerald-200 text-[12px] font-semibold">
              <Receipt className="h-4 w-4" />
              {isAr ? 'إيصال التحويل المرفق' : 'Attached transfer receipt'}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setViewingReceipt(true)}
              className="h-7 px-3 border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/10 text-[11px]"
              data-testid="wd-view-receipt"
            >
              <Eye className="h-3.5 w-3.5 me-1" />
              {isAr ? 'عرض الإيصال' : 'View receipt'}
            </Button>
          </div>
          {item.transfer_reference && (
            <p className="text-[11px] text-emerald-100/80 font-mono">
              {isAr ? 'مرجع الحوالة: ' : 'Ref: '}{item.transfer_reference}
            </p>
          )}
          {item.approval_note && (
            <p className="text-[11px] text-emerald-100/80">
              <strong className="me-1">{isAr ? 'ملاحظة:' : 'Note:'}</strong>{item.approval_note}
            </p>
          )}
          {item.override_note && (
            <div className="rounded bg-amber-500/10 border border-amber-500/30 p-2 mt-1">
              <p className="text-[11px] text-amber-200">
                <strong className="me-1">{isAr ? 'ملاحظة تجاوز:' : 'Override:'}</strong>
                {item.override_note}
              </p>
            </div>
          )}
          {item.ai_extracted && (
            <div className="mt-2">
              <AiExtractedSummary
                data={item.ai_extracted}
                match={item.ai_amount_match}
                isAr={isAr}
                compact
                readOnly
              />
            </div>
          )}
        </div>
      )}

      {/* Approve pane */}
      {isPending && !rejectMode && (
        <div className="md:col-span-2">
          <SectionCard
            title={isAr ? 'اعتماد ورفع إيصال التحويل' : 'Approve & upload transfer receipt'}
            icon={CheckCircle}
            tone="emerald"
          >
            <label
              htmlFor="wd-approve-file"
              className="flex items-center gap-2 px-3 py-3 rounded-lg bg-[#0a0a0a] border border-dashed border-emerald-500/30 hover:border-emerald-500/60 cursor-pointer transition-colors"
            >
              <UploadCloud className="h-4 w-4 text-emerald-300 shrink-0" />
              <span className="text-[12px] text-white truncate flex-1">
                {approveFile
                  ? approveFile.name
                  : (isAr ? 'اختر إيصال التحويل (PNG / JPEG / PDF — 8MB)' : 'Choose receipt (PNG / JPEG / PDF — 8MB)')}
              </span>
              {approveFile && <FileText className="h-3.5 w-3.5 text-emerald-300 shrink-0" />}
            </label>
            <input
              id="wd-approve-file"
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp,application/pdf"
              onChange={onFilePick}
              className="hidden"
              data-testid="wd-approve-file-input"
            />

            {aiLoading && (
              <div className="flex items-center gap-2 text-[11px] text-cyan-200 mt-2" data-testid="wd-ai-loading">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {isAr ? 'جاري قراءة الإيصال بالذكاء الاصطناعي…' : 'AI reading the receipt…'}
              </div>
            )}

            {aiData && !aiLoading && (
              <div className="mt-3">
                <AiExtractedSummary
                  data={aiData}
                  expectedAmount={amount}
                  match={amountMatches}
                  isAr={isAr}
                />
              </div>
            )}

            {aiData && !amountMatches && (
              <div className="mt-3">
                <label className="block text-[11px] font-semibold text-amber-200 mb-1">
                  {isAr
                    ? '⚠ مبلغ الإيصال لا يطابق مبلغ السحب — أدخل ملاحظة تجاوز صريحة قبل الاعتماد'
                    : '⚠ Receipt amount does NOT match — enter an explicit override note to approve'}
                </label>
                <textarea
                  rows={2}
                  value={overrideNote}
                  onChange={(e) => setOverrideNote(e.target.value)}
                  placeholder={isAr
                    ? 'مثال: الفرق 5 د.إ يعود لعمولة تحويل بنكية…'
                    : 'e.g. 5 AED difference due to bank transfer fee…'}
                  className="w-full px-3 py-2 bg-[#0a0a0a] border border-amber-500/30 rounded-lg text-white text-xs focus:outline-none focus:border-amber-500/60"
                  data-testid="wd-override-input"
                />
              </div>
            )}

            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
              <input
                type="text"
                value={approveRef}
                onChange={(e) => setApproveRef(e.target.value)}
                placeholder={isAr ? 'مرجع الحوالة (اختياري)' : 'Transfer reference (optional)'}
                className="w-full px-3 py-2 bg-[#0a0a0a] border border-white/10 rounded-lg text-white text-xs focus:outline-none focus:border-emerald-500/40"
                data-testid="wd-approve-ref"
              />
              <input
                type="text"
                value={approveNote}
                onChange={(e) => setApproveNote(e.target.value)}
                placeholder={isAr ? 'ملاحظة (اختياري)' : 'Note (optional)'}
                className="w-full px-3 py-2 bg-[#0a0a0a] border border-white/10 rounded-lg text-white text-xs focus:outline-none focus:border-emerald-500/40"
                data-testid="wd-approve-note"
              />
            </div>

            <div className="flex items-center gap-2 pt-3 mt-3 border-t border-white/5">
              <Button
                onClick={onApprove}
                disabled={!canApprove}
                className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="wd-approve-confirm"
              >
                {processing ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <>
                    <CheckCircle className="h-3.5 w-3.5 me-1.5" />
                    {isAr ? 'اعتماد ورفع الإيصال' : 'Approve & upload'}
                  </>
                )}
              </Button>
              <Button
                onClick={onOpenReject}
                variant="outline"
                className="border-red-500/30 text-red-300 hover:bg-red-500/10"
                data-testid="wd-open-reject"
              >
                <XCircle className="h-3.5 w-3.5 me-1.5" />
                {isAr ? 'رفض' : 'Reject'}
              </Button>
            </div>
          </SectionCard>
        </div>
      )}

      {isPending && rejectMode && (
        <div className="md:col-span-2">
          <SectionCard title={isAr ? 'رفض طلب السحب' : 'Reject withdrawal'} icon={XCircle} tone="red">
            {/* iter-206 — dropdown of admin-managed reasons */}
            <label className="block text-[11px] font-semibold text-red-200 mb-1">
              {isAr ? 'سبب الرفض (من القائمة المُدارة)' : 'Rejection reason (from managed list)'}
            </label>
            {rejectReasonsLoading ? (
              <div className="flex items-center gap-2 text-[11px] text-red-200/70 py-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {isAr ? 'جاري تحميل الأسباب…' : 'Loading reasons…'}
              </div>
            ) : rejectReasons.length === 0 ? (
              <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-2.5 text-[11px] text-amber-200">
                {isAr
                  ? 'لا توجد أسباب مضافة بعد. أضف أسباباً من "إعدادات القسم المالي ← القوائم المنسدلة ← أسباب رفض طلبات السحب".'
                  : 'No reasons added yet. Add some from "Finance Settings → Dropdown Lists → Withdrawal Rejection Reasons".'}
              </div>
            ) : (
              <select
                value={rejectReasonId}
                onChange={(e) => setRejectReasonId(e.target.value)}
                className="w-full px-3 py-2 bg-[#0a0a0a] border border-red-500/30 rounded-lg text-white text-xs focus:outline-none focus:border-red-400"
                data-testid="wd-reject-reason-select"
              >
                <option value="">{isAr ? '— اختر سبباً —' : '— Select a reason —'}</option>
                {rejectReasons.map((r) => (
                  <option key={r.id} value={r.id}>
                    {isAr ? r.label_ar : r.label_en}
                  </option>
                ))}
              </select>
            )}
            {/* Free-text notes (optional) */}
            <label className="block text-[11px] font-semibold text-red-200 mt-3 mb-1">
              {isAr ? 'ملاحظات إضافية (اختياري)' : 'Additional notes (optional)'}
            </label>
            <textarea
              rows={2}
              value={rejectNotes}
              onChange={(e) => setRejectNotes(e.target.value)}
              placeholder={isAr ? 'أي تفاصيل توضّح السبب للتاجر…' : 'Any details clarifying the reason to the merchant…'}
              className="w-full px-3 py-2 bg-[#0a0a0a] border border-red-500/30 rounded-lg text-white text-xs focus:outline-none focus:border-red-400"
              data-testid="wd-reject-notes"
            />
            <div className="flex items-center gap-2 pt-3">
              <Button
                onClick={onReject}
                disabled={processing || !rejectReasonId}
                className="bg-red-600 hover:bg-red-700 text-white flex-1"
                data-testid="wd-reject-confirm"
              >
                {processing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (isAr ? 'تأكيد الرفض' : 'Confirm rejection')}
              </Button>
              <Button
                onClick={onCancelReject}
                variant="outline"
                className="border-white/10 text-white"
                data-testid="wd-reject-cancel"
              >
                {isAr ? 'إلغاء' : 'Cancel'}
              </Button>
            </div>
          </SectionCard>
        </div>
      )}
    </div>
  );
}

// ─── Agent classification badges ───────────────────────────────────
// iter-208 — always show the account type + verification state (positive AND
// negative — admin needs to KNOW whether an account is unverified).
function AgentBadges({ user, isAr }) {
  if (!user) return <span className="text-[10px] text-[#71717A]">—</span>;
  const badges = [];

  // Account type: verified agent > agent > merchant > customer
  if (user.is_verified_agent) {
    badges.push({ key: 'verified-agent', icon: BadgeCheck, label: isAr ? 'وكيل موثّق' : 'Verified Agent',
      cls: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30' });
  } else if (user.is_agent) {
    badges.push({ key: 'agent', icon: Shield, label: isAr ? 'وكيل غير موثّق' : 'Agent (unverified)',
      cls: 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30' });
  } else {
    // Not an agent → show merchant role explicitly
    const role = (user.role || '').toLowerCase();
    if (role === 'business' || role === 'merchant') {
      badges.push({ key: 'merchant', icon: Building2, label: isAr ? 'تاجر' : 'Merchant',
        cls: 'bg-amber-500/15 text-amber-200 border-amber-500/30' });
    } else if (role === 'customer') {
      badges.push({ key: 'customer', icon: User, label: isAr ? 'عميل' : 'Customer',
        cls: 'bg-slate-500/15 text-slate-200 border-slate-500/30' });
    } else {
      badges.push({ key: 'merchant-default', icon: Building2, label: isAr ? 'تاجر' : 'Merchant',
        cls: 'bg-amber-500/15 text-amber-200 border-amber-500/30' });
    }
  }

  // KYC — always show (positive OR negative)
  if (user.kyc_verified) {
    badges.push({ key: 'kyc-ok', icon: ShieldCheck, label: isAr ? 'KYC موثّق' : 'KYC verified',
      cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' });
  } else if (user._source !== 'bank_beneficiary') {
    // Only show "unverified KYC" if we actually have a real user (not the bank-fallback).
    badges.push({ key: 'kyc-no', icon: AlertTriangle, label: isAr ? 'KYC غير موثّق' : 'KYC not verified',
      cls: 'bg-red-500/15 text-red-300 border-red-500/30' });
  }

  if (badges.length === 0) return <span className="text-[10px] text-[#71717A]">—</span>;
  return (
    <>
      {badges.map((b) => (
        <span
          key={b.key}
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-medium ${b.cls}`}
          data-testid={`wd-badge-${b.key}`}
        >
          <b.icon className="h-3 w-3" />
          {b.label}
        </span>
      ))}
    </>
  );
}

// ─── AI extraction summary ─────────────────────────────────────────
// iter-209 — `readOnly` skips the "approve auto-disabled — add override note"
// call-to-action, which is only relevant BEFORE approval. Once the admin has
// already approved (with an override note) the message becomes stale/misleading.
function AiExtractedSummary({ data, expectedAmount, match, isAr, compact = false, readOnly = false }) {
  if (!data) return null;
  const okReceipt = !!data.is_receipt;
  const extractedAmount = floorAED(data.amount);

  return (
    <div
      className={`rounded-xl border p-3 ${
        !okReceipt
          ? 'bg-red-500/10 border-red-500/30'
          : match
            ? 'bg-emerald-500/10 border-emerald-500/30'
            : 'bg-amber-500/10 border-amber-500/30'
      }`}
      data-testid="wd-ai-summary"
    >
      <div className="flex items-center gap-1.5 text-[12px] font-semibold mb-2">
        <Sparkles className={`h-3.5 w-3.5 ${
          !okReceipt ? 'text-red-300' : match ? 'text-emerald-300' : 'text-amber-300'
        }`} />
        <span className={
          !okReceipt ? 'text-red-200' : match ? 'text-emerald-200' : 'text-amber-200'
        }>
          {!okReceipt
            ? (isAr ? 'AI رفض الملف' : 'AI rejected')
            : match
              ? (isAr ? 'AI: المبلغ مطابق ✓' : 'AI: amount matches ✓')
              : (isAr ? 'AI: المبلغ غير مطابق ✗' : 'AI: amount mismatch ✗')}
        </span>
      </div>

      {!okReceipt && (
        <p className="text-[11px] text-red-200/90 mb-1" data-testid="wd-ai-reject-reason">
          {data.reject_reason || (isAr ? 'الملف ليس إيصال تحويل صحيح' : 'File is not a valid transfer receipt')}
        </p>
      )}

      {okReceipt && (
        <div className={`grid ${compact ? 'grid-cols-2 gap-2' : 'grid-cols-2 md:grid-cols-3 gap-3'} text-[12px]`}>
          <AiField label={isAr ? 'مبلغ الإيصال' : 'Receipt amount'} value={fmtAED(extractedAmount)} strong testid="wd-ai-amount" />
          {typeof expectedAmount === 'number' && (
            <AiField label={isAr ? 'المتوقع' : 'Expected'} value={fmtAED(expectedAmount)} strong testid="wd-ai-expected" />
          )}
          {data.reference && <AiField label={isAr ? 'المرجع' : 'Reference'} value={data.reference} mono testid="wd-ai-ref" />}
          {data.date && <AiField label={isAr ? 'التاريخ' : 'Date'} value={data.date} mono testid="wd-ai-date" />}
          {data.bank_name && <AiField label={isAr ? 'البنك' : 'Bank'} value={data.bank_name} testid="wd-ai-bank" />}
          {data.iban && <AiField label={isAr ? 'IBAN المستفيد' : 'Beneficiary IBAN'} value={data.iban} mono testid="wd-ai-iban" />}
        </div>
      )}

      {!match && okReceipt && !readOnly && (
        <div className="mt-2 flex items-start gap-1.5 text-[11px] text-amber-200/90">
          <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
          <span>
            {isAr
              ? 'الاعتماد معطّل تلقائياً — أدخل ملاحظة تجاوز صريحة للمتابعة.'
              : 'Approve is auto-disabled — add an explicit override note to continue.'}
          </span>
        </div>
      )}
    </div>
  );
}

function AiField({ label, value, mono, strong, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-[#A1A1AA]/70 mb-0.5">{label}</div>
      <div
        className={`text-white break-all ${strong ? 'font-bold text-[14px]' : ''} ${mono ? 'font-mono' : ''}`}
        data-testid={testid}
      >
        {value}
      </div>
    </div>
  );
}

// ─── Receipt viewer modal ──────────────────────────────────────────
function ReceiptViewerModal({ item, isAr, onClose }) {
  const mime = item.receipt_mime || '';
  const isPdf = mime.toLowerCase() === 'application/pdf';
  const dataUrl = `data:${mime};base64,${item.receipt_file}`;
  const filename = item.receipt_name || (isPdf ? 'receipt.pdf' : 'receipt.png');

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="wd-receipt-modal"
    >
      <div
        className="relative w-full max-w-3xl max-h-[90vh] bg-[#0a0a0a] border border-white/10 rounded-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-emerald-400" />
            <div>
              <h3 className="text-white font-semibold text-sm">
                {isAr ? 'إيصال التحويل' : 'Transfer receipt'}
              </h3>
              <p className="text-[10px] text-[#A1A1AA]">{filename}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <a
              href={dataUrl}
              download={filename}
              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white transition-colors"
              data-testid="wd-receipt-download"
            >
              <Download className="h-4 w-4" />
            </a>
            <button
              onClick={onClose}
              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white transition-colors"
              data-testid="wd-receipt-close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto bg-black/30 flex items-center justify-center p-4">
          {isPdf ? (
            <object data={dataUrl} type="application/pdf" className="w-full h-[75vh]">
              <div className="text-center text-[#A1A1AA] text-sm p-8">
                {isAr ? 'المتصفح لا يدعم عرض PDF مباشرة.' : 'Your browser cannot display the PDF inline.'}
              </div>
            </object>
          ) : (
            <img src={dataUrl} alt={filename} className="max-w-full max-h-[75vh] rounded-lg object-contain" />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Shared blocks ─────────────────────────────────────────────────
function EditDateModal({
  isAr, dateVal, setDateVal, reason, setReason, saving,
  onCancel, onSave, originalIso, currentIso, historyCount,
}) {
  const fmt = (raw) => {
    if (!raw) return '—';
    try { return new Date(raw).toLocaleString(isAr ? 'ar-AE' : 'en-GB'); }
    catch { return raw; }
  };
  return (
    <div
      className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onCancel}
      data-testid="wd-detail-edit-date-modal"
    >
      <div
        className="w-full max-w-md rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-[#0f1a14] to-[#0a0a0a] p-5"
        onClick={(e) => e.stopPropagation()}
        dir={isAr ? 'rtl' : 'ltr'}
      >
        <div className="flex items-center gap-2 mb-4">
          <div className="w-9 h-9 rounded-lg bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
            <Pencil className="h-4 w-4 text-emerald-300" />
          </div>
          <div>
            <h3 className="text-white font-bold text-base">
              {isAr ? 'تعديل تاريخ طلب السحب' : 'Edit withdrawal date'}
            </h3>
            <p className="text-[11px] text-emerald-200/70 mt-0.5">
              {isAr ? 'سيتم إشعار التاجر تلقائياً بالتعديل' : 'The merchant will be notified automatically'}
            </p>
          </div>
        </div>

        <div className="text-[11px] text-[#A1A1AA] bg-white/[0.03] border border-white/5 rounded-lg p-3 mb-4 space-y-1">
          <div className="flex justify-between gap-2">
            <span className="text-white/50">{isAr ? 'التاريخ الحالي' : 'Current'}:</span>
            <span className="text-white/80" dir="ltr">{fmt(currentIso)}</span>
          </div>
          {originalIso && originalIso !== currentIso && (
            <div className="flex justify-between gap-2">
              <span className="text-white/50">{isAr ? 'التاريخ الأصلي' : 'Original'}:</span>
              <span className="text-amber-300/80" dir="ltr">{fmt(originalIso)}</span>
            </div>
          )}
          {historyCount > 0 && (
            <div className="flex justify-between gap-2">
              <span className="text-white/50">{isAr ? 'عدد التعديلات السابقة' : 'Previous edits'}:</span>
              <span className="text-white/70">{historyCount}</span>
            </div>
          )}
        </div>

        <label className="block text-[12px] text-white/70 mb-1.5">
          {isAr ? 'التاريخ والوقت الجديد' : 'New date & time'} <span className="text-emerald-400">*</span>
        </label>
        <input
          type="datetime-local"
          value={dateVal}
          onChange={(e) => setDateVal(e.target.value)}
          className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-3 py-2 text-white text-sm mb-3"
          data-testid="wd-detail-edit-date-input"
        />

        <label className="block text-[12px] text-white/70 mb-1.5">
          {isAr ? 'سبب التعديل (يظهر للتاجر)' : 'Reason for edit (shown to merchant)'} <span className="text-emerald-400">*</span>
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder={isAr ? 'مثال: تصحيح تاريخ الطلب لتوافقه مع الحوالة الأصلية' : 'e.g. Correcting to match the original transfer date'}
          className="w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-3 py-2 text-white text-sm resize-none mb-4"
          data-testid="wd-detail-edit-date-reason"
        />

        <div className="flex justify-end gap-2">
          <Button
            onClick={onCancel}
            variant="outline"
            className="border-white/10 text-white/70 hover:bg-white/5"
            data-testid="wd-detail-edit-date-cancel"
          >
            {isAr ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button
            onClick={onSave}
            disabled={saving || !reason.trim() || !dateVal}
            className="bg-emerald-500 hover:bg-emerald-600 text-white gap-2 disabled:opacity-40"
            data-testid="wd-detail-edit-date-save"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
            {isAr ? 'حفظ وإرسال إشعار' : 'Save & notify'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function SlaBanner({ item, isAr }) {
  const isPending = item.status === 'pending';
  const isDelayed = !!item.is_delayed;
  const daysRemaining = typeof item.days_remaining === 'number' ? item.days_remaining : null;
  const daysElapsed   = typeof item.days_elapsed === 'number' ? item.days_elapsed : null;
  const slaDays = item.sla_days || 15;
  const deadlineStr = useMemo(() => {
    if (!item.deadline_at) return null;
    try { return new Date(item.deadline_at).toLocaleDateString(isAr ? 'ar-AE' : 'en-GB'); }
    catch { return item.deadline_at; }
  }, [item.deadline_at, isAr]);

  if (!isPending || daysRemaining === null) return null;

  let tone = 'cyan';
  let title;
  let sub;
  let Icon = Clock;
  if (isDelayed) {
    tone = 'red';
    Icon = AlertTriangle;
    title = isAr
      ? `⚠ الطلب متأخر — تجاوز مدة الالتزام (${slaDays} يوم)`
      : `⚠ Delayed — exceeded the ${slaDays}-day SLA`;
    sub = isAr
      ? `مرّ على وصول الطلب ${daysElapsed} يوم • كان يجب اتخاذ قرار بحلول ${deadlineStr}`
      : `${daysElapsed}d elapsed • deadline was ${deadlineStr}`;
  } else if (daysRemaining <= 3) {
    tone = 'amber';
    title = isAr
      ? `⏳ يتبقى ${daysRemaining} يوم على انتهاء الـSLA`
      : `⏳ Only ${daysRemaining} day(s) left in SLA`;
    sub = isAr
      ? `مدة الالتزام ${slaDays} يوم من تاريخ الطلب • ينتهي في ${deadlineStr}`
      : `${slaDays}-day SLA from request date • ends ${deadlineStr}`;
  } else {
    tone = 'cyan';
    title = isAr
      ? `⏱ يتبقى ${daysRemaining} يوم على انتهاء الـSLA`
      : `⏱ ${daysRemaining} days left in SLA`;
    sub = isAr
      ? `مدة الالتزام ${slaDays} يوم من تاريخ الطلب • ينتهي في ${deadlineStr}`
      : `${slaDays}-day SLA from request date • ends ${deadlineStr}`;
  }

  const cls = {
    red:   'bg-red-500/10 border-red-500/30 text-red-100',
    amber: 'bg-amber-500/10 border-amber-500/30 text-amber-100',
    cyan:  'bg-cyan-500/10 border-cyan-500/30 text-cyan-100',
  }[tone];
  const iconCls = {
    red: 'text-red-300', amber: 'text-amber-300', cyan: 'text-cyan-300',
  }[tone];

  return (
    <div
      className={`md:col-span-2 rounded-xl border p-4 ${cls}`}
      data-testid="wd-sla-banner"
    >
      <div className="flex items-start gap-3">
        <Icon className={`h-5 w-5 shrink-0 mt-0.5 ${iconCls}`} />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-[13px]" data-testid="wd-sla-title">{title}</div>
          <div className="text-[11px] opacity-80 mt-0.5" data-testid="wd-sla-sub">{sub}</div>
        </div>
      </div>
    </div>
  );
}

function SectionCard({ title, icon: Icon, tone = 'default', children }) {
  const toneCls = {
    default: 'border-white/10 bg-white/[0.02]',
    emerald: 'border-emerald-500/20 bg-emerald-500/[0.03]',
    red:     'border-red-500/20 bg-red-500/[0.03]',
  }[tone] || 'border-white/10 bg-white/[0.02]';
  return (
    <div className={`rounded-xl border ${toneCls} p-4`}>
      <div className="flex items-center gap-1.5 mb-3">
        <Icon className="h-3.5 w-3.5 text-[#A1A1AA]" />
        <span className="text-[11px] uppercase tracking-wide text-[#A1A1AA] font-medium">{title}</span>
      </div>
      {children}
    </div>
  );
}

function ProfileRow({ icon: Icon, value, label, mono, testid }) {
  return (
    <div className="flex items-center gap-1.5">
      <Icon className="h-3 w-3 text-[#71717A] shrink-0" />
      {label && <span className="text-[10px] text-[#71717A]">{label}</span>}
      <span
        className={`text-white/90 truncate ${mono ? 'font-mono' : ''}`}
        title={value}
        data-testid={testid}
      >
        {value}
      </span>
    </div>
  );
}

function BankRow({ label, value, mono, testid }) {
  if (!value) return null;
  return (
    <div className="flex items-center justify-between gap-3 text-[12px]">
      <span className="text-[#A1A1AA] shrink-0">{label}</span>
      <span
        className={`text-white text-end truncate ${mono ? 'font-mono' : ''}`}
        title={value}
        data-testid={testid}
      >
        {value}
      </span>
    </div>
  );
}
